'use client'
import React from 'react';
import { Question, Answer } from '@/app/types';
import { IconImage, IconAudio, IconTrash, IconTarget, IconCheck, IconX, IconChevronUp, IconChevronDown, IconPlus } from '@/assets/icons/gameicon';

interface QuestionItemProps {
  index: number;
  question: Question;
  totalQuestions: number;
  isActive: boolean;
  onSelect: () => void;
  onUpdate: (id: string, updates: Partial<Question>) => void;
  onDelete: (id: string) => void;
  onUpdateAnswer: (questionId: string, answerId: string, updates: Partial<Answer>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddAnswer: (questionId: string) => void;
  onDeleteAnswer: (questionId: string, answerId: string) => void;
}

const QuestionItem: React.FC<QuestionItemProps> = ({ 
  index, 
  question, 
  totalQuestions, 
  isActive,
  onSelect,
  onUpdate, 
  onDelete, 
  onUpdateAnswer, 
  onMoveUp, 
  onMoveDown,
  onAddAnswer,
  onDeleteAnswer
}) => {
  return (
    <div 
      className={`flex gap-4 mb-6 relative transition-all duration-200 p-2 rounded-xl -ml-2 ${isActive ? 'bg-yellow-50' : ''}`}
      onClick={(e) => {
        // Prevent triggering select if clicking inside inputs to avoid focus fighting, 
        // though usually bubbling is fine.
        onSelect();
      }}
    >
      <div className="pt-4 font-bold text-gray-500 text-lg">{index + 1}.</div>
      
      <div className={`flex-1 bg-gray-100 rounded-lg p-4 border shadow-sm transition-colors ${isActive ? 'border-yellow-400 ring-1 ring-yellow-400' : 'border-gray-200'}`}>
        {/* Header with Position Info */}
        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
          <span className="font-semibold text-gray-600">คำถาม</span>
          
          {/* Editable Position Inputs */}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white px-2 py-1 rounded border border-gray-200">
            <IconTarget className={`w-4 h-4 ${isActive ? 'text-red-500' : 'text-gray-400'}`} />
            <span className="text-xs font-bold text-gray-400 uppercase mr-1">Pos</span>
            
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">X:</span>
              <input 
                type="number" 
                step="0.01" 
                min="0" 
                max="1" 
                className="w-16 border border-gray-300 rounded px-1 py-0.5 text-right focus:outline-none focus:border-blue-500 text-xs"
                value={question.x}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 1) {
                    onUpdate(question.id, { x: val });
                  }
                }}
              />
            </div>
            
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">Y:</span>
              <input 
                type="number" 
                step="0.01" 
                min="0" 
                max="1" 
                className="w-16 border border-gray-300 rounded px-1 py-0.5 text-right focus:outline-none focus:border-blue-500 text-xs"
                value={question.y}
                onChange={(e) => {
                   const val = parseFloat(e.target.value);
                   if (!isNaN(val) && val >= 0 && val <= 1) {
                     onUpdate(question.id, { y: val });
                   }
                }}
              />
            </div>
          </div>
        </div>

        {/* Question Input */}
        <div className="relative mb-4 group">
          <input
            type="text"
            className="w-full border border-gray-400 rounded p-2 pr-20 focus:outline-none focus:border-blue-500 transition-colors"
            placeholder="ใส่คำถามที่นี่..."
            value={question.text}
            onChange={(e) => onUpdate(question.id, { text: e.target.value })}
            onFocus={onSelect}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1 text-gray-400">
            <button className="p-1 hover:text-blue-500 hover:bg-gray-100 rounded">
                <IconImage className="w-5 h-5" />
            </button>
            <button className="p-1 hover:text-blue-500 hover:bg-gray-100 rounded">
                <IconAudio className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Answers Section */}
        <div className="space-y-2">
            <span className="font-semibold text-gray-600 text-sm">คำตอบ</span>
            <div className="flex flex-col gap-3">
                {question.answers.map((answer) => (
                    <div key={answer.id} className="flex items-center gap-2">
                      <div className="flex-1 flex items-center bg-white border border-gray-300 rounded overflow-hidden">
                          {/* Label (A/B) */}
                          <div className="pl-3 pr-2 py-2 text-gray-600 font-medium min-w-[32px] text-center">
                              {answer.label}
                          </div>
                          
                          {/* Correct Toggle */}
                          <div className="pr-2">
                            <button 
                              onClick={(e) => {
                                  e.stopPropagation(); // Prevent card selection flicker if needed
                                  onUpdateAnswer(question.id, answer.id, { isCorrect: !answer.isCorrect });
                              }}
                              className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${answer.isCorrect ? 'border-green-500 bg-green-50 text-green-600' : 'border-red-300 bg-red-50 text-red-500'}`}
                            >
                                  {answer.isCorrect ? <IconCheck className="w-4 h-4" /> : <IconX className="w-4 h-4" />}
                            </button>
                          </div>

                          {/* Input */}
                          <div className="flex-1 relative">
                              <input 
                                  type="text"
                                  className="w-full p-2 pr-16 focus:outline-none text-sm"
                                  placeholder={`คำตอบ ${answer.label}`}
                                  value={answer.text}
                                  onChange={(e) => onUpdateAnswer(question.id, answer.id, { text: e.target.value })}
                                  onFocus={onSelect}
                              />
                              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5 text-blue-400">
                                  <button className="p-1 hover:text-blue-600 hover:bg-gray-50 rounded">
                                      <IconImage className="w-4 h-4" />
                                  </button>
                                  <button className="p-1 hover:text-blue-600 hover:bg-gray-50 rounded">
                                      <IconAudio className="w-4 h-4" />
                                  </button>
                              </div>
                          </div>
                      </div>
                      
                      {/* Delete Answer Button */}
                      <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAnswer(question.id, answer.id);
                        }}
                        className="p-2 text-gray-300 hover:text-red-400 transition-colors"
                        title="ลบคำตอบ"
                      >
                         <IconTrash className="w-4 h-4" />
                      </button>
                    </div>
                ))}

                {/* Add Answer Button */}
                <button 
                  onClick={(e) => {
                      e.stopPropagation();
                      onAddAnswer(question.id);
                  }}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <IconPlus className="w-4 h-4" />
                  เพิ่มคำตอบเพิ่มเติม
                </button>
            </div>
        </div>

      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-10 items-center">
        <button 
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={index === 0}
            className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
            title="Move Up"
        >
            <IconChevronUp className="w-6 h-6" />
        </button>
        <button 
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={index === totalQuestions - 1}
            className={`text-gray-400 hover:text-gray-600 ${index === totalQuestions - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
            title="Move Down"
        >
            <IconChevronDown className="w-6 h-6" />
        </button>
        <div className="h-2"></div> {/* Spacer */}
        <button 
            onClick={(e) => { e.stopPropagation(); onDelete(question.id); }}
            className="text-red-300 hover:text-red-500"
            title="Delete"
        >
            <IconTrash className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default QuestionItem;